import { useEffect, useState } from 'react';

/**
 * Themes (D-24): light, semi-dark ("dim"), dark, or follow the OS. The choice is per
 * device (localStorage), applied to <html data-theme> before the first paint by the
 * inline script in index.html and kept in sync here.
 */
export type Theme = 'system' | 'light' | 'dim' | 'dark';
export const THEMES: Theme[] = ['system', 'light', 'dim', 'dark'];
export const THEME_KEY = 'crmsy.theme';

const listeners = new Set<() => void>();
const media = typeof window !== 'undefined' && 'matchMedia' in window ? window.matchMedia('(prefers-color-scheme: dark)') : null;

export function getTheme(): Theme {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return THEMES.includes(v as Theme) ? (v as Theme) : 'system';
  } catch {
    return 'system';
  }
}

/** The theme actually shown ('system' resolved against the OS). */
export function resolvedTheme(t: Theme = getTheme()): 'light' | 'dim' | 'dark' {
  if (t !== 'system') return t;
  return media?.matches ? 'dark' : 'light';
}

export function applyTheme(): void {
  const r = resolvedTheme();
  if (r === 'light') delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = r;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', r === 'light' ? '#d87025' : r === 'dim' ? '#34302b' : '#1d1a17');
}

export function setTheme(t: Theme): void {
  try {
    if (t === 'system') localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, t);
  } catch {
    /* private mode: the choice lasts for this page only */
  }
  applyTheme();
  for (const fn of listeners) fn();
}

media?.addEventListener('change', () => {
  if (getTheme() === 'system') {
    applyTheme();
    for (const fn of listeners) fn();
  }
});

export function useTheme(): [Theme, (t: Theme) => void] {
  const [theme, set] = useState<Theme>(getTheme);
  useEffect(() => {
    const fn = () => set(getTheme());
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return [theme, setTheme];
}
